import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  VStack,
  Heading,
  Text,
  Button,
  useToast,
  Radio,
  RadioGroup,
  Stack,
  Card,
  CardBody,
  CardFooter,
  Textarea,
  Progress,
  HStack,
  UnorderedList,
  ListItem,
  Spinner,
  Center,
  Select,
  Grid,
  GridItem,
  List,
  ListIcon,
  Divider,
  Badge,
} from '@chakra-ui/react';
import { ChevronRightIcon } from '@chakra-ui/icons';
import axios from 'axios';
import { API_BASE_URL } from '../config';

const API_URL = API_BASE_URL;

function Topic() {
  const { week, subtopicId } = useParams();
  const [topic, setTopic] = useState(null);
  const [subtopic, setSubtopic] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [selectedAnswer, setSelectedAnswer] = useState('');
  const [matchingAnswers, setMatchingAnswers] = useState({});
  const [feedback, setFeedback] = useState('');
  const [isCorrect, setIsCorrect] = useState(null);
  const [loading, setLoading] = useState(true);
  const [startingQuiz, setStartingQuiz] = useState(false);
  const [submittingAnswer, setSubmittingAnswer] = useState(false);
  const [generatingQuestion, setGeneratingQuestion] = useState('');
  const [streamingProgress, setStreamingProgress] = useState('');
  const [streamingFeedback, setStreamingFeedback] = useState('');
  const [totalQuestionsAttempted, setTotalQuestionsAttempted] = useState(0);
  const navigate = useNavigate();
  const toast = useToast();

  useEffect(() => {
    const fetchTopic = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/topics/${week}`);
        setTopic(response.data);
        const subtopicResponse = await axios.get(`${API_URL}/api/topics/${week}/subtopics/${subtopicId}`);
        setSubtopic(subtopicResponse.data);
      } catch (error) {
        console.error('Error fetching topic:', error);
        toast({
          title: 'Error',
          description: 'Failed to fetch topic details',
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      } finally {
        setLoading(false);
      }
    };

    fetchTopic();
  }, [week, subtopicId, toast]);

  const startQuiz = async () => {
    setStartingQuiz(true);
    setGeneratingQuestion('');
    setStreamingProgress('');
    try {
      const response = await fetch(`${API_URL}/api/quiz/start`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          weekNumber: parseInt(week),
          subtopicId,
          model: 'gpt-4o-mini'
        })
      });

      if (!response.ok) {
        throw new Error('Failed to start quiz');
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      let jsonBuffer = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        const chunk = decoder.decode(value, { stream: true });
        buffer += chunk;
        jsonBuffer += chunk;
        
        setStreamingProgress(buffer);

        try {
          const data = JSON.parse(jsonBuffer);
          setQuestions(data.questions);
          setCurrentQuestionIndex(0);
          setFeedback('');
          setSelectedAnswer('');
          setMatchingAnswers({});
          setIsCorrect(null);
          setStreamingProgress('');
          setTotalQuestionsAttempted(prev => prev + 1);
          break;
        } catch (e) {
          continue;
        }
      }
    } catch (error) {
      console.error('Error starting quiz:', error);
      toast({
        title: 'Error',
        description: 'Failed to start quiz',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setStartingQuiz(false);
    }
  };

  const handleMatchingChange = (leftItem, rightItem) => {
    setMatchingAnswers(prev => ({
      ...prev,
      [leftItem]: rightItem
    }));
  };

  const submitAnswer = async () => {
    if (!questions[currentQuestionIndex]) return;

    const currentQuestion = questions[currentQuestionIndex];
    let answerToSubmit = selectedAnswer;

    if (currentQuestion.type === 'matching') {
      answerToSubmit = matchingAnswers;
    }

    if (!answerToSubmit && currentQuestion.type !== 'case_study') {
      toast({
        title: 'Error',
        description: 'Please provide an answer',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    setSubmittingAnswer(true);
    setStreamingFeedback('');
    setFeedback(null);
    setIsCorrect(null);
    
    try {
      const response = await fetch(`${API_URL}/api/quiz/submit`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          weekNumber: parseInt(week),
          subtopicId,
          question: currentQuestion,
          answer: answerToSubmit,
        })
      });

      if (!response.ok) {
        throw new Error('Failed to submit answer');
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      let jsonBuffer = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        const chunk = decoder.decode(value, { stream: true });
        buffer += chunk;
        jsonBuffer += chunk;
        
        setStreamingFeedback(buffer);

        try {
          const data = JSON.parse(jsonBuffer);
          setFeedback(data.feedback);
          setIsCorrect(data.isCorrect);
          setStreamingFeedback('');
          break;
        } catch (e) {
          continue;
        }
      }
    } catch (error) {
      console.error('Error submitting answer:', error);
      toast({
        title: 'Error',
        description: 'Failed to submit answer',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setSubmittingAnswer(false);
    }
  };

  const nextQuestion = async () => {
    if (currentQuestionIndex < questions.length - 1) {
      setCurrentQuestionIndex(currentQuestionIndex + 1);
      setSelectedAnswer('');
      setMatchingAnswers({});
      setFeedback('');
      setIsCorrect(null);
    } else {
      // Generate new question instead of showing completion
      setStartingQuiz(true);
      try {
        const response = await fetch(`${API_URL}/api/quiz/start`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            weekNumber: parseInt(week),
            subtopicId,
            model: 'gpt-4o-mini'
          })
        });

        if (!response.ok) {
          throw new Error('Failed to generate new question');
        }

        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';
        let jsonBuffer = '';

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          const chunk = decoder.decode(value, { stream: true });
          buffer += chunk;
          jsonBuffer += chunk;
          
          setStreamingProgress(buffer);

          try {
            const data = JSON.parse(jsonBuffer);
            setQuestions(data.questions);
            setCurrentQuestionIndex(0);
            setFeedback('');
            setSelectedAnswer('');
            setMatchingAnswers({});
            setIsCorrect(null);
            setStreamingProgress('');
            setTotalQuestionsAttempted(prev => prev + 1);
            break;
          } catch (e) {
            continue;
          }
        }
      } catch (error) {
        console.error('Error generating new question:', error);
        toast({
          title: 'Error',
          description: 'Failed to generate new question',
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      } finally {
        setStartingQuiz(false);
      }
    }
  };

  if (loading) {
    return (
      <Center h="100vh">
        <VStack spacing={4}>
          <Spinner size="xl" color="blue.500" />
          <Text>Loading topic...</Text>
        </VStack>
      </Center>
    );
  }

  if (!topic || !subtopic) {
    return <Text>Topic or subtopic not found</Text>;
  }

  const currentQuestion = questions[currentQuestionIndex];

  return (
    <Box>
      <VStack spacing={6} align="stretch">
        <Box>
          <Badge colorScheme="blue" mb={2}>Week {week}</Badge>
          <Heading size="lg">{topic.topic}</Heading>
        </Box>

        <Card>
          <CardBody>
            <VStack align="stretch" spacing={4}>
              <Heading size="md">{subtopic.title}</Heading>
              
              <Box>
                <Heading size="sm" mb={3} color="blue.600">Theory Summary:</Heading>
                <Text>{subtopic.theory_summary}</Text>
              </Box>

              <Box>
                <Heading size="sm" mb={3} color="blue.600">Key Concepts:</Heading>
                <List spacing={3}>
                  {subtopic.key_concepts.map((concept, index) => (
                    <ListItem key={index} display="flex" alignItems="flex-start">
                      <ListIcon as={ChevronRightIcon} color="blue.500" mt={1} />
                      <Text>{concept}</Text>
                    </ListItem>
                  ))}
                </List>
              </Box>

              <Divider my={4} />
            </VStack>
          </CardBody>
          <CardFooter>
            <Button
              colorScheme="blue"
              size="lg"
              onClick={startQuiz}
              isLoading={startingQuiz}
              loadingText="Starting Quiz..."
            >
              Start Quiz
            </Button>
          </CardFooter>
        </Card>

        {streamingProgress && (
          <Card>
            <CardBody>
              <VStack spacing={4}>
                <Text>Generating questions...</Text>
                <Box 
                  fontFamily="monospace" 
                  whiteSpace="pre-wrap" 
                  p={4} 
                  bg="gray.50" 
                  borderRadius="md"
                  maxH="300px"
                  overflowY="auto"
                >
                  {streamingProgress}
                </Box>
              </VStack>
            </CardBody>
          </Card>
        )}

        {!questions.length ? null : (
          <Card>
            <CardBody>
              <VStack spacing={4} align="stretch">
                <HStack justify="space-between">
                  <Text>Question {totalQuestionsAttempted}</Text>
                  {currentQuestion && (
                    <Text color="blue.500">{currentQuestion.type?.toUpperCase()}</Text>
                  )}
                </HStack>
                <Progress value={(currentQuestionIndex + 1) / questions.length * 100} />
                {currentQuestion && (
                  <>
                    {console.log('Full question object:', currentQuestion)}
                    {console.log('Question type:', currentQuestion.type)}
                    {console.log('Question type lowercase:', currentQuestion.type?.toLowerCase())}
                    
                    <Text fontWeight="bold">{currentQuestion.question}</Text>
                    
                    <RadioGroup value={selectedAnswer} onChange={setSelectedAnswer}>
                      <Stack>
                        {currentQuestion.options && Object.entries(currentQuestion.options).map(([key, value]) => (
                          <Radio key={key} value={key}>
                            {value}
                          </Radio>
                        ))}
                      </Stack>
                    </RadioGroup>
                  </>
                )}
              </VStack>
            </CardBody>
            <CardFooter>
              {!feedback ? (
                <Button 
                  colorScheme="blue" 
                  onClick={submitAnswer}
                  isLoading={submittingAnswer}
                  loadingText="Submitting..."
                >
                  Submit Answer
                </Button>
              ) : (
                <Button colorScheme="green" onClick={nextQuestion}>
                  Next Question
                </Button>
              )}
            </CardFooter>
          </Card>
        )}

        {streamingFeedback && (
          <Card>
            <CardBody>
              <VStack spacing={4}>
                <Text>Generating feedback...</Text>
                <Box 
                  fontFamily="monospace" 
                  whiteSpace="pre-wrap" 
                  p={4} 
                  bg="gray.50" 
                  borderRadius="md"
                  maxH="300px"
                  overflowY="auto"
                >
                  {streamingFeedback}
                </Box>
              </VStack>
            </CardBody>
          </Card>
        )}

        {feedback && (
          <Box mt={4}>
            <Card>
              <CardBody>
                <VStack align="stretch" spacing={4}>
                  <Box>
                    <Heading size="md" mb={2}>Score: {feedback.score}/100</Heading>
                  </Box>
                  
                  <Box>
                    <Heading size="sm" mb={2}>What you got right:</Heading>
                    <UnorderedList>
                      {feedback.feedback.correct.map((item, index) => (
                        <ListItem key={index}>{item}</ListItem>
                      ))}
                    </UnorderedList>
                  </Box>

                  <Box>
                    <Heading size="sm" mb={2}>Areas for improvement:</Heading>
                    <UnorderedList>
                      {feedback.feedback.incorrect.map((item, index) => (
                        <ListItem key={index}>{item}</ListItem>
                      ))}
                    </UnorderedList>
                  </Box>

                  <Box>
                    <Heading size="sm" mb={2}>Explanation:</Heading>
                    <Text>{feedback.feedback.explanation}</Text>
                  </Box>

                  <Box>
                    <Heading size="sm" mb={2}>Suggestions for improvement:</Heading>
                    <UnorderedList>
                      {feedback.feedback.suggestions.map((item, index) => (
                        <ListItem key={index}>{item}</ListItem>
                      ))}
                    </UnorderedList>
                  </Box>
                </VStack>
              </CardBody>
            </Card>
          </Box>
        )}

        <Button onClick={() => navigate('/')}>Back to Home</Button>
      </VStack>
    </Box>
  );
}

export default Topic; 